/**
 * `txplanselector` — a Form.io host component for the Angular treatment-plan selector.
 *
 * Selection data remains outside Form.io submission data and persists through the existing
 * `MTPSelections` save path. At runtime the submission host supplies `options.txPlan`; authoring
 * surfaces without that bridge render a static placeholder.
 *
 * Direct `@formio/js` usage is a sanctioned exception to the v2 AI rules (formio-library layer).
 */
import { Components } from '@formio/js';
import { escapeHtml } from '@shared/utils/escape-html';
import { formioFieldComponent } from '../formio-angular-host';

export interface TxPlanBridge {
  mount(hostEl: HTMLElement): TxPlanMountHandle;
  refresh(handle: TxPlanMountHandle): void;
  destroy(handle: TxPlanMountHandle): void;
}

export type TxPlanMountHandle = unknown;

export const TX_PLAN_SELECTOR_TYPE = 'txplanselector';
export const TX_PLAN_SELECTOR_KEY = 'txPlanSelector';

const TX_PLAN_HOST_REF = 'txPlanHost';

export function shouldPreserveTxPlanHostOnRedraw(state: {
  hasBridge: boolean;
  hasHandle: boolean;
  hostConnected: boolean;
}): boolean {
  return state.hasBridge && state.hasHandle && state.hostConnected;
}

export function txPlanSelectorSchema(): Record<string, unknown> {
  return {
    type: TX_PLAN_SELECTOR_TYPE,
    key: TX_PLAN_SELECTOR_KEY,
    label: 'Master Treatment Plan',
    input: false,
    persistent: false,
    tableView: false,
    hideLabel: true,
  };
}

const FieldComponent = formioFieldComponent<{
  txPlan?: TxPlanBridge;
  txPlanPlaceholder?: boolean;
  txPlanUnavailable?: boolean;
}>();

let translateFn: ((key: string) => string) | null = null;

function tr(key: string, fallback: string): string {
  return translateFn?.(key) ?? fallback;
}

export class TxPlanSelectorFormioComponent extends FieldComponent {
  private txPlanHandle: TxPlanMountHandle | null = null;
  private mountedHostEl: HTMLElement | null = null;

  static schema(...extend: unknown[]): Record<string, unknown> {
    return FieldComponent.schema(txPlanSelectorSchema(), ...extend);
  }

  static get builderInfo(): Record<string, unknown> {
    return {
      title: tr('forms.txPlanSelector.builderTitle', 'Treatment Plan Selector'),
      group: 'integrated',
      icon: 'list',
      weight: 20,
      schema: TxPlanSelectorFormioComponent.schema(),
    };
  }

  static editForm(): Record<string, unknown> {
    return {
      components: [
        {
          type: 'tabs',
          key: 'tabs',
          components: [
            {
              label: tr('forms.txPlanSelector.editDisplayTab', 'Display'),
              key: 'display',
              weight: 0,
              components: [
                {
                  type: 'textfield',
                  key: 'label',
                  label: tr('forms.txPlanSelector.editLabel', 'Label'),
                  input: true,
                },
                {
                  type: 'textfield',
                  key: 'key',
                  label: tr('forms.txPlanSelector.editKey', 'Property Name (Key)'),
                  input: true,
                },
              ],
            },
          ],
        },
      ],
    };
  }

  render(): string {
    if (this.options?.txPlan) {
      return super.render(`<div ref="${TX_PLAN_HOST_REF}"></div>`);
    }
    if (this.builderMode || this.options?.txPlanPlaceholder) {
      const title = tr('forms.txPlanSelector.placeholderTitle', 'Master Treatment Plan');
      const body = tr(
        'forms.txPlanSelector.placeholderBody',
        'Staff will select treatment-plan goals and objectives here when this form is used for a client.'
      );
      return super.render(
        `<div class="tx-plan-selector-placeholder tw-rounded-md tw-border tw-border-sage tw-bg-mint tw-p-3">
           <div class="tw-mb-1 tw-font-semibold tw-text-foreground">${escapeHtml(title)}</div>
           <p class="text-small tw-text-muted-foreground tw-mb-0">${escapeHtml(body)}</p>
         </div>`
      );
    }
    if (this.options?.txPlanUnavailable) {
      const body = tr(
        'forms.txPlanSelector.unavailable',
        "Treatment plan selection is unavailable because you do not have access to the patient's Master Treatment Plan."
      );
      return super.render(
        `<div role="status" class="tx-plan-selector-unavailable tw-rounded-md tw-border tw-border-sage tw-bg-mint tw-p-3">
           <p class="text-small tw-text-muted-foreground tw-mb-0">${escapeHtml(body)}</p>
         </div>`
      );
    }
    return super.render('');
  }

  attach(element: HTMLElement): Promise<void> {
    if (this.options?.txPlan) {
      this.loadRefs(element, { [TX_PLAN_HOST_REF]: 'single' });
      this.mountTxPlanHost();
    }
    return super.attach(element);
  }

  // Preserve a connected Angular host across Form.io conditional/calculated redraws so editable
  // goal/objective state is refreshed in place instead of being torn down and re-seeded.
  redraw(): Promise<void> {
    const bridge = this.options?.txPlan;
    const preserve = shouldPreserveTxPlanHostOnRedraw({
      hasBridge: !!bridge,
      hasHandle: this.txPlanHandle != null,
      hostConnected: !!this.mountedHostEl?.isConnected,
    });
    if (bridge && preserve) {
      bridge.refresh(this.txPlanHandle);
      return Promise.resolve();
    }
    return super.redraw();
  }

  destroy(...args: unknown[]): void {
    if (this.txPlanHandle != null && this.options?.txPlan) {
      this.options.txPlan.destroy(this.txPlanHandle);
      this.txPlanHandle = null;
      this.mountedHostEl = null;
    }
    super.destroy(...args);
  }

  private mountTxPlanHost(): void {
    const bridge = this.options?.txPlan;
    const hostEl = this.refs?.[TX_PLAN_HOST_REF];
    if (!bridge || !hostEl) return;
    if (this.txPlanHandle != null) {
      bridge.destroy(this.txPlanHandle);
      this.txPlanHandle = null;
    }
    this.txPlanHandle = bridge.mount(hostEl);
    this.mountedHostEl = hostEl;
  }
}

let registered = false;

export function registerTxPlanSelectorComponent(translate?: (key: string) => string): void {
  if (translate) translateFn = translate;
  if (registered) return;
  Components.addComponent(TX_PLAN_SELECTOR_TYPE, TxPlanSelectorFormioComponent);
  registered = true;
}
